import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  DataTable,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  TextInput,
  useToast,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { api, describeError } from '../lib/api';
import { useResource } from '../lib/hooks';
import { formatDateTime, relativeTime } from '../lib/format';

interface User {
  id: string;
  email: string;
  username: string | null;
  displayName: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  lockedUntil: string | null;
  mustChangePw: boolean;
}

const ROLE_DESCRIPTIONS: Record<string, string> = {
  OWNER:
    'Użytkownicy, ustawienia bezpieczeństwa, sieć wewnętrzna, przełączniki awaryjne, pełny audyt.',
  ADMIN: 'Dostawcy, konta, operacje, aplikacje, klucze i polityki.',
  OPERATOR: 'Monitoring, testy połączeń, konsola testowa, bezpieczne włączanie i wygaszanie.',
  VIEWER: 'Tylko odczyt pulpitów i dzienników.',
};

export function UsersPage({ role }: { role: string }) {
  const users = useResource<{ items: User[] }>('/api/admin/users');
  const sessions = useResource<{
    items: Array<{
      id: string;
      user: { email: string };
      ip: string | null;
      lastSeenAt: string;
      expiresAt: string;
    }>;
  }>(role === 'OWNER' ? '/api/admin/sessions' : null);
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: '',
    username: '',
    displayName: '',
    role: 'VIEWER',
    password: '',
  });
  const isOwner = role === 'OWNER';

  return (
    <>
      <PageHeader
        title="Użytkownicy i role"
        description="Sesje panelu są całkowicie odrębne od kluczy aplikacji i tokenów deweloperskich."
        actions={
          isOwner ? (
            <Button variant="primary" onClick={() => setOpen(true)}>
              Dodaj użytkownika
            </Button>
          ) : undefined
        }
      />

      <Card title="Zakres ról" tight>
        <div className="ah-card-body">
          <dl className="ah-kv">
            {Object.entries(ROLE_DESCRIPTIONS).map(([name, description]) => (
              <div key={name} style={{ display: 'contents' }}>
                <dt>
                  <Badge tone={name === 'OWNER' ? 'info' : 'neutral'}>{name}</Badge>
                </dt>
                <dd>{description}</dd>
              </div>
            ))}
          </dl>
        </div>
      </Card>

      {users.loading ? (
        <Skeleton rows={4} />
      ) : (
        <Card title="Użytkownicy" tight>
          <DataTable
            rows={users.data?.items ?? []}
            rowKey={(row) => row.id}
            empty={<EmptyState title="Brak użytkowników" />}
            columns={[
              {
                key: 'user',
                header: 'Użytkownik',
                render: (row) => (
                  <>
                    <div>
                      {row.displayName}
                      {row.username && <code className="ah-mono"> ({row.username})</code>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--ah-text-muted)' }}>{row.email}</div>
                  </>
                ),
              },
              {
                key: 'role',
                header: 'Rola',
                render: (row) => (
                  <Badge tone={row.role === 'OWNER' ? 'info' : 'neutral'}>{row.role}</Badge>
                ),
              },
              {
                key: 'status',
                header: 'Status',
                render: (row) => (
                  <>
                    <Badge tone={row.isActive ? 'ok' : 'danger'}>
                      {row.isActive ? 'aktywny' : 'wyłączony'}
                    </Badge>
                    {row.lockedUntil && new Date(row.lockedUntil).getTime() > Date.now() && (
                      <div>
                        <Badge tone="warn">zablokowany</Badge>
                      </div>
                    )}
                    {row.mustChangePw && (
                      <div>
                        <Badge tone="warn">wymaga zmiany hasła</Badge>
                      </div>
                    )}
                  </>
                ),
              },
              {
                key: 'lastLogin',
                header: 'Ostatnie logowanie',
                render: (row) => (row.lastLoginAt ? relativeTime(row.lastLoginAt) : 'nigdy'),
              },
              {
                key: 'created',
                header: 'Utworzono',
                render: (row) => formatDateTime(row.createdAt),
              },
              {
                key: 'actions',
                header: '',
                align: 'right',
                render: (row) =>
                  isOwner ? (
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      {row.lockedUntil && (
                        <Button
                          size="sm"
                          onClick={async () => {
                            try {
                              await api.patch(`/api/admin/users/${row.id}`, { unlock: true });
                              users.reload();
                            } catch (error) {
                              const described = describeError(error);
                              toast.error(described.title, described.body);
                            }
                          }}
                        >
                          Odblokuj
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant={row.isActive ? 'danger' : 'default'}
                        onClick={async () => {
                          try {
                            await api.patch(`/api/admin/users/${row.id}`, {
                              isActive: !row.isActive,
                            });
                            toast.success(
                              row.isActive ? 'Użytkownik wyłączony' : 'Użytkownik włączony',
                            );
                            users.reload();
                          } catch (error) {
                            const described = describeError(error);
                            toast.error(described.title, described.body);
                          }
                        }}
                      >
                        {row.isActive ? 'Wyłącz' : 'Włącz'}
                      </Button>
                    </div>
                  ) : null,
              },
            ]}
          />
        </Card>
      )}

      {isOwner && (
        <Card title="Aktywne sesje panelu" tight>
          <DataTable
            rows={sessions.data?.items ?? []}
            rowKey={(row) => row.id}
            empty={<EmptyState title="Brak aktywnych sesji" />}
            columns={[
              { key: 'user', header: 'Użytkownik', render: (row) => row.user.email },
              { key: 'ip', header: 'IP', render: (row) => row.ip ?? '—' },
              {
                key: 'seen',
                header: 'Ostatnia aktywność',
                render: (row) => relativeTime(row.lastSeenAt),
              },
              { key: 'expires', header: 'Wygasa', render: (row) => formatDateTime(row.expiresAt) },
              {
                key: 'actions',
                header: '',
                align: 'right',
                render: (row) => (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={async () => {
                      try {
                        await api.delete(`/api/admin/sessions/${row.id}`);
                        sessions.reload();
                      } catch (error) {
                        const described = describeError(error);
                        toast.error(described.title, described.body);
                      }
                    }}
                  >
                    Zakończ
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      )}

      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Nowy użytkownik"
        description="Użytkownik będzie musiał zmienić hasło przy pierwszym logowaniu."
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Anuluj</Button>
            <Button
              variant="primary"
              disabled={!form.email || !form.displayName || form.password.length < 9}
              onClick={async () => {
                try {
                  // Pusty login oznacza "konto tylko na e-mail" — nie wysyłamy
                  // pustego ciągu, bo ten nie przeszedłby walidacji wzorca.
                  const { username, ...rest } = form;
                  await api.post('/api/admin/users', username.trim() ? form : rest);
                  toast.success('Użytkownik utworzony', form.email);
                  setOpen(false);
                  setForm({
                    email: '',
                    username: '',
                    displayName: '',
                    role: 'VIEWER',
                    password: '',
                  });
                  users.reload();
                } catch (error) {
                  const described = describeError(error);
                  toast.error(described.title, described.body);
                }
              }}
            >
              Utwórz
            </Button>
          </>
        }
      >
        <Field label="Adres e-mail">
          {(id) => (
            <TextInput
              id={id}
              type="email"
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
            />
          )}
        </Field>
        <Field label="Login (opcjonalny — pozwala logować się bez adresu e-mail)">
          {(id) => (
            <TextInput
              id={id}
              placeholder="np. admin"
              value={form.username}
              onChange={(event) => setForm({ ...form, username: event.target.value })}
            />
          )}
        </Field>
        <Field label="Nazwa wyświetlana">
          {(id) => (
            <TextInput
              id={id}
              value={form.displayName}
              onChange={(event) => setForm({ ...form, displayName: event.target.value })}
            />
          )}
        </Field>
        <Field label="Rola">
          {(id) => (
            <Select
              id={id}
              value={form.role}
              onChange={(event) => setForm({ ...form, role: event.target.value })}
            >
              {Object.keys(ROLE_DESCRIPTIONS).map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label="Hasło tymczasowe"
          hint="Minimum 9 znaków i trzy z czterech grup: małe, wielkie, cyfry, znaki specjalne."
        >
          {(id) => (
            <TextInput
              id={id}
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(event) => setForm({ ...form, password: event.target.value })}
            />
          )}
        </Field>
        <Callout>
          Przekaż hasło kanałem innym niż e-mail. Użytkownik zmieni je przy pierwszym logowaniu.
        </Callout>
      </Modal>
    </>
  );
}
